import isEqual from 'react-fast-compare';

// Check for exact equality between two arrays (values CAN BE in a different order)
export function arrayEquals(array1: (string | number)[], array2: (string | number)[]) {
  if (array1.length === 0 && array2.length === 0) {
    return true;
  }

  if (array1.length !== array2.length) {
    return false;
  }

  // Merge both arrays and keep unique values only
  const unionArray = Array.from(new Set([...array1, ...array2]));

  // The length should be the same
  return unionArray.length === array1.length;
}

// Check for exact equality between two arrays (values MUST BE in the same order)
export function arrayStrictEquals<T = string>(array1: T[], array2: T[]) {
  return isEqual(array1, array2);
}

// Remove duplicates from given array
export function arrayUnique<T>(array: T[]) {
  return Array.from(new Set([...array]));
}

// Remove duplicates objects from given array base on a unique given field
export function arrayObjFieldsUnique<T>(array: T[], fieldsCallback: (item: T) => string) {
  const newArray: T[] = [];
  array.forEach((item) => {
    if (!newArray.map((i) => fieldsCallback(i)).includes(fieldsCallback(item))) {
      newArray.push(item);
    }
  });
  return newArray;
}

// Return array in chunks of given length
export function arrayChunks<T>(array: T[], chunkLength: number): T[][] {
  const chunks = [];
  let i = 0;
  const n = array.length;

  while (i < n) {
    chunks.push(array.slice(i, i += chunkLength));
  }

  return chunks;
}

export function arrayFlat<T>(array: T[][]): T[] {
  return array.reduce<T[]>((acc, arr) => {
    const newAcc = acc.concat(arr);
    return newAcc;
  }, []);
}

export function arrayTimes(times: number): number[] {
  return [...Array(times).keys()];
}

export function arraySum(array: number[]): number {
  return array.reduce((a, b) => a + b, 0);
}

export function arrayMove<T>(array: T[], fromIndex: number, toIndex: number) {
  const newArray = [...array];
  const element = newArray[fromIndex];
  newArray.splice(fromIndex, 1);
  newArray.splice(toIndex, 0, element);
  return newArray;
}

export function arrayLast<T>(array: T[]) {
  return array[array.length - 1];
}

export function arrayInsert<T>(array: T[], newItems: T | T[], position?: number) {
  const newArray: T[] = [...array];
  newArray.splice(position || 0, 0, ...(Array.isArray(newItems) ? newItems : [newItems]));
  return newArray;
}

export function arraySortByNumberInString(array: string[]) {
  return [...array].sort((a, b) => Number(a.replace(/^\D+/g, '')) - Number(b.replace(/^\D+/g, '')));
}

/**
 * Count the number of occurrences of an item within the array
 */
export function arrayCount(array: string[], item: string) {
  return array.filter((str) => str === item).length;
}

export function arrayIntersection<T = string | number>(arr1: T[], arr2: T[]) {
  return arr1.filter((str) => arr2.includes(str));
}

/**
 * Return all elements from arr1 that are NOT in arr2
 */
export function arrayDiff<T = string | number>(arr1: T[], arr2: T[]) {
  return arr1.filter((str) => !arr2.includes(str));
}

/**
 * Return both:
 * - all elements from arr1 that are NOT in arr2
 * - all elements from arr2 that are NOT in arr1
 */
export function arraySymmetricDiff<T = string | number>(arr1: T[], arr2: T[]) {
  const arr1Missing = arrayDiff(arr1, arr2);
  const arr2Missing = arrayDiff(arr2, arr1);

  // Note: within a Worker/Blob method names are lost during minification when using the spread operator, use array concat here
  return ([] as T[]).concat(arr1Missing, arr2Missing);
}

export function arrayFilterOut<T>(array: T[], arrayToRemove: T[]) {
  return array.filter((str) => !arrayToRemove.includes(str));
}

export function arrayToggle(array: string[], item: string) {
  if (array.includes(item)) {
    return array.filter((i) => i !== item);
  }
  return [...array, item];
}

/**
 * Return a slice of the array around center value
 *
 * @param array array of values
 * @param value center value of the slice from the array
 * @param count number of values to return around center value
 * @returns slice of values around the center value
 */
export function arrayAround<T>(array: T[], value: T, count: number) {
  if (count === 1) return [value];

  const valueIndex = array.findIndex((v) => v === value);
  if (valueIndex === -1) return [];

  let startIndex = valueIndex - Math.floor((count) / 2);
  let endIndex = valueIndex + Math.ceil((count) / 2);

  if (startIndex < 0) {
    endIndex += (startIndex * -1);
    startIndex = 0;
  } else if (endIndex > array.length) {
    startIndex -= endIndex - array.length;
    endIndex = array.length;
  }

  return array.slice(startIndex, endIndex);
}

export function arrayClosest(arr: number[], value: number, force?: 'below' | 'above') {
  let sortedArr = arr;
  if (force === 'below') sortedArr = arr.sort((a, b) => a - b);
  if (force === 'above') sortedArr = arr.sort((a, b) => a - b).reverse();

  return sortedArr.reduce((prev, curr) => {
    if (Math.abs(curr - value) < Math.abs(prev - value)) {
      if (force === 'below') {
        return curr > value ? prev : curr;
      }
      if (force === 'above') {
        return curr < value ? prev : curr;
      }
      return curr;
    }
    return prev;
  });
}

export function arrayLongest(arr: string[]) {
  return arr.sort((a, b) => b.length - a.length)[0];
}
